import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { API, NEED_TYPES, TIMINGS } from "../../lib/site";
import { Reveal } from "./Reveal";

const EMPTY = {
    name: "",
    company: "",
    phone: "",
    email: "",
    postal_code: "",
    city: "",
    need_type: "",
    timing: "",
    description: "",
    visit_requested: false,
    visit_address: "",
    consent: false,
    website: "",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RE = /^(?:\+33[\s.-]?|0)[1-9](?:[\s.-]?\d{2}){4}$/;

const inputCls =
    "w-full rounded-xl border border-zinc-300 bg-white px-4 py-3.5 text-base text-ink outline-none transition-colors focus:border-signal focus:ring-2 focus:ring-signal/20 placeholder:text-zinc-400";

const Field = ({ label, required, error, testid, children }) => (
    <div>
        <label className="mb-1.5 block text-sm font-semibold text-ink">
            {label} {required && <span className="text-signal">*</span>}
        </label>
        {children}
        {error && (
            <p data-testid={testid} className="mt-1.5 text-sm font-medium text-red-600">
                {error}
            </p>
        )}
    </div>
);

const PillGroup = ({ legend, options, value, onChange, testid, error, errorTestid }) => (
    <fieldset>
        <legend className="mb-2.5 text-sm font-semibold text-ink">
            {legend} <span className="text-signal">*</span>
        </legend>
        <div className="flex flex-wrap gap-2" role="radiogroup" data-testid={testid}>
            {options.map((opt) => (
                <button
                    key={opt}
                    type="button"
                    role="radio"
                    aria-checked={value === opt}
                    data-testid={`${testid}-option-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    onClick={() => onChange(opt)}
                    className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                        value === opt
                            ? "border-signal bg-signal text-ink"
                            : "border-zinc-300 bg-white text-zinc-700 hover:border-signal/60"
                    }`}
                >
                    {opt}
                </button>
            ))}
        </div>
        {error && (
            <p data-testid={errorTestid} className="mt-1.5 text-sm font-medium text-red-600">
                {error}
            </p>
        )}
    </fieldset>
);

const validate = (f) => {
    const e = {};
    if (f.name.trim().length < 2) e.name = "Veuillez indiquer votre nom et prénom.";
    if (f.company.trim().length < 2) e.company = "Veuillez indiquer votre société.";
    if (!PHONE_RE.test(f.phone.trim())) e.phone = "Veuillez saisir un numéro de téléphone valide.";
    if (!EMAIL_RE.test(f.email.trim())) e.email = "Veuillez saisir une adresse email valide.";
    if (!/^\d{5}$/.test(f.postal_code.trim())) e.postal_code = "Code postal à 5 chiffres requis.";
    if (f.city.trim().length < 2) e.city = "Veuillez indiquer la ville du chantier.";
    if (!f.need_type) e.need_type = "Sélectionnez votre type de besoin.";
    if (!f.timing) e.timing = "Sélectionnez un délai souhaité.";
    if (f.visit_requested && f.visit_address.trim().length < 5)
        e.visit_address = "Veuillez indiquer l'adresse du chantier ou du rendez-vous.";
    if (!f.consent) e.consent = "Votre consentement est nécessaire pour être recontacté.";
    return e;
};

export const LeadForm = () => {
    const [form, setForm] = useState(EMPTY);
    const [errors, setErrors] = useState({});
    const [state, setState] = useState("idle");
    const [serverError, setServerError] = useState("");

    const set = (key) => (ev) => {
        const value = ev?.target ? (ev.target.type === "checkbox" ? ev.target.checked : ev.target.value) : ev;
        setForm((f) => ({ ...f, [key]: value }));
        setErrors((e) => ({ ...e, [key]: undefined }));
    };

    const submit = async (ev) => {
        ev.preventDefault();
        const e = validate(form);
        setErrors(e);
        if (Object.keys(e).length) return;
        setState("sending");
        setServerError("");
        try {
            await axios.post(`${API}/leads`, { ...form, email: form.email.trim() });
            setState("success");
        } catch (err) {
            setState("idle");
            const detail = err.response?.data?.detail;
            const msg = Array.isArray(detail)
                ? "Certains champs sont invalides. Vérifiez le formulaire."
                : typeof detail === "string"
                  ? detail
                  : "Une erreur est survenue. Veuillez réessayer.";
            setServerError(msg);
        }
    };

    return (
        <section id="besoin" data-testid="lead-form-section" className="bg-paper py-24 text-ink lg:py-36">
            <div className="mx-auto max-w-4xl px-5 lg:px-8">
                <Reveal className="mb-12 text-center">
                    <p className="mb-5 font-mono text-xs uppercase tracking-[0.3em] text-signal">
                        05 — Votre besoin
                    </p>
                    <h2 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl">
                        Parlons de votre chantier
                    </h2>
                    <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-zinc-600 sm:text-lg">
                        Décrivez-nous votre besoin. Un commercial NEBOORZY vous recontactera pour
                        étudier votre demande et organiser un rendez-vous si nécessaire.
                    </p>
                    <p
                        data-testid="lead-form-national-note"
                        className="mx-auto mt-5 w-fit rounded-full border border-signal/40 bg-signal/5 px-5 py-2.5 text-sm font-semibold text-signal"
                    >
                        Nos commerciaux se déplacent partout en France pour étudier vos besoins
                        directement sur le terrain.
                    </p>
                </Reveal>
                <Reveal delay={0.1}>
                    <div className="rounded-3xl border border-zinc-200 bg-white p-6 shadow-[0_24px_80px_-24px_rgba(11,12,14,0.25)] sm:p-10">
                        {state === "success" ? (
                            <div data-testid="lead-form-success-message" className="py-10 text-center">
                                <CheckCircle2 className="mx-auto h-14 w-14 text-signal" />
                                <h3 className="mt-6 font-display text-2xl font-bold">
                                    Demande transmise
                                </h3>
                                <p className="mx-auto mt-3 max-w-md text-base text-zinc-600">
                                    Votre demande a bien été transmise. Un commercial NEBOORZY
                                    prendra contact avec vous prochainement.
                                </p>
                            </div>
                        ) : (
                            <form data-testid="lead-form" onSubmit={submit} noValidate>
                                <div className="grid gap-5 sm:grid-cols-2">
                                    <Field label="Nom et prénom" required error={errors.name} testid="error-name">
                                        <input data-testid="lead-form-name" className={inputCls} value={form.name} onChange={set("name")} autoComplete="name" placeholder="Jean Martin" />
                                    </Field>
                                    <Field label="Société" required error={errors.company} testid="error-company">
                                        <input data-testid="lead-form-company" className={inputCls} value={form.company} onChange={set("company")} autoComplete="organization" placeholder="SARL Bâtiment Dupont" />
                                    </Field>
                                    <Field label="Téléphone" required error={errors.phone} testid="error-phone">
                                        <input data-testid="lead-form-phone" type="tel" className={inputCls} value={form.phone} onChange={set("phone")} autoComplete="tel" placeholder="06 12 34 56 78" />
                                    </Field>
                                    <Field label="Email" required error={errors.email} testid="error-email">
                                        <input data-testid="lead-form-email" type="email" className={inputCls} value={form.email} onChange={set("email")} autoComplete="email" placeholder="jean@entreprise.fr" />
                                    </Field>
                                    <Field label="Code postal du chantier" required error={errors.postal_code} testid="error-postal">
                                        <input data-testid="lead-form-postal-code" inputMode="numeric" className={inputCls} value={form.postal_code} onChange={set("postal_code")} placeholder="69000" />
                                    </Field>
                                    <Field label="Ville du chantier" required error={errors.city} testid="error-city">
                                        <input data-testid="lead-form-city" className={inputCls} value={form.city} onChange={set("city")} placeholder="Lyon" />
                                    </Field>
                                </div>
                                <div className="mt-7 space-y-7">
                                    <PillGroup legend="Votre besoin" options={NEED_TYPES} value={form.need_type} onChange={set("need_type")} testid="lead-form-need-type" error={errors.need_type} errorTestid="error-need-type" />
                                    <PillGroup legend="Quand avez-vous besoin de notre intervention ?" options={TIMINGS} value={form.timing} onChange={set("timing")} testid="lead-form-timing" error={errors.timing} errorTestid="error-timing" />
                                    <Field label="Décrivez votre chantier ou le matériel recherché" error={errors.description} testid="error-description">
                                        <textarea
                                            data-testid="lead-form-description"
                                            rows={5}
                                            className={`${inputCls} resize-y`}
                                            value={form.description}
                                            onChange={set("description")}
                                            placeholder="Exemple : type de chantier, besoin de [matériel/prestation], durée estimée, contraintes particulières…"
                                        />
                                    </Field>
                                    <div>
                                        <label className="flex cursor-pointer items-start gap-3 text-sm font-medium text-ink">
                                            <input
                                                type="checkbox"
                                                data-testid="lead-form-visit-checkbox"
                                                checked={form.visit_requested}
                                                onChange={set("visit_requested")}
                                                className="mt-0.5 h-5 w-5 accent-signal"
                                            />
                                            Je souhaite qu'un commercial se déplace sur site
                                        </label>
                                        <AnimatePresence initial={false}>
                                            {form.visit_requested && (
                                                <motion.div
                                                    initial={{ opacity: 0, height: 0 }}
                                                    animate={{ opacity: 1, height: "auto" }}
                                                    exit={{ opacity: 0, height: 0 }}
                                                    transition={{ duration: 0.25 }}
                                                    className="overflow-hidden"
                                                >
                                                    <div className="mt-4">
                                                        <Field label="Adresse du chantier / lieu de rendez-vous" required error={errors.visit_address} testid="error-visit-address">
                                                            <input data-testid="lead-form-visit-address" className={inputCls} value={form.visit_address} onChange={set("visit_address")} placeholder="Adresse complète du chantier" />
                                                        </Field>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                    <div>
                                        <label className="flex cursor-pointer items-start gap-3 text-sm text-zinc-600">
                                            <input
                                                type="checkbox"
                                                data-testid="lead-form-consent-checkbox"
                                                checked={form.consent}
                                                onChange={set("consent")}
                                                className="mt-0.5 h-5 w-5 accent-signal"
                                            />
                                            <span>
                                                J'accepte que NEBOORZY utilise les informations transmises
                                                afin de me recontacter au sujet de ma demande.{" "}
                                                <span className="text-signal">*</span>
                                            </span>
                                        </label>
                                        {errors.consent && (
                                            <p data-testid="error-consent" className="mt-1.5 text-sm font-medium text-red-600">
                                                {errors.consent}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <input
                                    type="text"
                                    name="website"
                                    data-testid="lead-form-honeypot"
                                    value={form.website}
                                    onChange={set("website")}
                                    tabIndex={-1}
                                    autoComplete="off"
                                    aria-hidden="true"
                                    className="pointer-events-none absolute -left-[9999px] top-0 h-px w-px opacity-0"
                                />
                                {serverError && (
                                    <p data-testid="lead-form-server-error" className="mt-5 rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                                        {serverError}
                                    </p>
                                )}
                                <button
                                    type="submit"
                                    data-testid="lead-form-submit-button"
                                    disabled={state === "sending"}
                                    className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-signal px-8 py-4 text-base font-bold text-ink transition-colors hover:bg-signal-dark disabled:opacity-60"
                                >
                                    {state === "sending" ? (
                                        <>
                                            <Loader2 className="h-5 w-5 animate-spin" />
                                            Envoi en cours…
                                        </>
                                    ) : (
                                        <>
                                            Envoyer ma demande à un commercial
                                            <Send className="h-5 w-5" />
                                        </>
                                    )}
                                </button>
                                <p className="mt-4 text-center text-xs text-zinc-500">
                                    Vos informations sont utilisées uniquement pour traiter votre
                                    demande. Voir notre politique de confidentialité en bas de page.
                                </p>
                            </form>
                        )}
                    </div>
                </Reveal>
            </div>
        </section>
    );
};
